import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { useEffect, useMemo, useState } from 'react';
import { createApi } from './api.js';
import type { RuntimeConfig } from './config.js';
import { Home } from './Home.js';
import { SlowBar } from './Loader.js';
import { SignIn } from './SignIn.js';

export function App({ config }: { config: RuntimeConfig }) {
  // Local development (vite dev) has no Cognito: the local API trusts a stand-in user.
  const devUser = 'devUser' in config ? config.devUser : undefined;
  const [signedIn, setSignedIn] = useState<boolean | null>(devUser ? true : null);
  const api = useMemo(() => {
    if ('userPoolId' in config) {
      Amplify.configure({
        Auth: {
          Cognito: { userPoolId: config.userPoolId, userPoolClientId: config.userPoolClientId },
        },
      });
    }
    return createApi(config.apiUrl, devUser);
  }, [config, devUser]);

  useEffect(() => {
    if (devUser) return;
    getCurrentUser()
      .then(() => setSignedIn(true))
      .catch(() => setSignedIn(false));
  }, [devUser]);

  // Resume the resting database straight away, so it is awake by the time the person has signed in.
  useEffect(() => {
    void fetch(`${config.apiUrl}/wake`).catch(() => undefined);
  }, [config.apiUrl]);

  if (signedIn === null) return null;
  if (!signedIn) return <SignIn onSignedIn={() => setSignedIn(true)} />;
  const onSignOut = devUser ? () => undefined : () => void signOut().then(() => setSignedIn(false));
  return (
    <>
      <SlowBar />
      <Home api={api} onSignOut={onSignOut} />
    </>
  );
}
